import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { KolSearchForm } from '@trinity/chat';
import type { API } from '@trinity/chat';
import type { AxiosError } from 'axios';
import type { ChatState } from '@trinity/chat-components/components/Chat.State/interfaces.chat';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { useDebounceValue } from '@trinity/chat-components/hooks/useDebounceValue';

type UseKolSearchParams = {
  chatIdentifier: string;
  identifier: string;
  form: Partial<KolSearchForm>;
  recommendations: API.Kol.SubmitKolSearch.Request['recommendations'];
  brandIdentifier: string;
  filters: API.Kol.SubmitKolSearch.Request['filters'];
  sort: API.Kol.SubmitKolSearch.Request['sort'];
  enabled?: boolean;
  dispatch: (action: ChatState.Action) => void;
};

export const useKolSearch = (rawParams: UseKolSearchParams) => {

  const params = useDebounceValue(rawParams, 500);

  const apiClient = useChatApiClient();

  const query = useQuery({
    queryKey: ['kolSearch', params.chatIdentifier, params.identifier, params.form, params.filters, params.sort],
    queryFn: async () => {
      // Convert the full form to the API's expected format
      const apiForm: API.Kol.SubmitKolSearch.Request['form'] = {
        name: params.form.name || '',
        ...(params.form.kolId && { kolId: params.form.kolId }),
        ...(params.form.location && { location: params.form.location }),
      };

      // Auto-sort by distance ascending when location is present and no explicit sort is set
      let sort = params.sort;
      if (params.form.location && (!sort || Object.values(sort).every(value => !value))) {
        sort = { sortBy: 'distance', sortDir: 'asc' };
      }

      const searchData: API.Kol.SubmitKolSearch.Request = {
        chatIdentifier: params.chatIdentifier,
        identifier: params.identifier,
        form: apiForm,
        recommendations: params.recommendations,
        brandIdentifier: params.brandIdentifier,
        filters: params.filters,
        sort: sort,
      };

      const result = await apiClient.kolSearch.submitKolSearch(searchData);
      return result;
    },
    enabled: params.enabled ?? false,
  });

  // Handle success/error dispatching
  useEffect(() => {
    // Only dispatch if the query is enabled and successful
    if (query.isSuccess && query.data && params.enabled) {
      params.dispatch({
        type: 'kol-search-results-available',
        payload: {
          chatInstance: query.data.chatInstance,
          result: query.data.result,
          identifier: params.identifier,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.isSuccess, query.data, params.dispatch, params.identifier, params.enabled]);

  useEffect(() => {
    if (query.isError && query.error) {
      const error = query.error as AxiosError;
      const traceId = error.response?.headers?.['X-Sentry-Trace-Id'] as string;
      params.dispatch({
        type: 'kol-search-form-submission-errored',
        payload: {
          identifier: params.identifier,
          traceId,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.isError, query.error, params.dispatch, params.identifier]);

  return query;
};
